// The Give-recognition composer, shared.
//
// Lifted out of RecognitionFeed so Conversations can open the SAME dialog
// rather than growing its own. There is one recognition composer in the
// product: same fields, same validation, same create call, same feed — which
// is also why a recognition given from a conversation shows up on the
// Recognition page without anything extra.
//
// Two seams make the reuse work, and nothing else changed:
//   * searchRecipients — Conversations restricts the recipient to people in
//     THAT conversation; everywhere else it is the whole directory.
//   * source — where the recognition came from, recorded on the record.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Award, Loader2, Send, X, Sparkles, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { VoiceInputButton } from '@/features/shared/components/VoiceInputButton';
import { getApiErrorMessage } from '@/lib/apiError';
import * as rec from '@/services/recognitionService';
import * as workforce from '@/services/workforceService';

/** What was created, for a surface that wants to announce it. */
export interface PostedRecognition {
  id?: string;
  recipientName: string;
  alsoNames: string[];
  categoryLabel: string;
  title: string;
  message: string;
}

/** Where this recognition came from. Defaults to a manual post from the feed. */
export interface RecognitionSource {
  domain: string;          // 'manual' | 'conversation' | …
  entity_type?: string;
  entity_id?: string;
  comment_id?: string;
  ai_suggested?: boolean;
}

const initials = (name: string) =>
  (name || '?').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const AV_COLORS = ['#7C3AED', '#DC2626', '#059669', '#2563EB', '#D9711E', '#0891B2', '#DB2777'];
const colorFor = (seed: string) => AV_COLORS[[...seed].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length];

export const Avatar: React.FC<{ name: string; seed?: string; size?: number }> = ({ name, seed, size = 40 }) => (
  <span
    className="inline-grid place-items-center rounded-full text-white font-semibold shrink-0"
    style={{ width: size, height: size, fontSize: size * 0.36, background: colorFor(seed || name) }}
  >
    {initials(name)}
  </span>
);

export function recipientLabel(
  item: { recipients?: { name: string }[]; recipient_name?: string },
  t: (k: string, d?: string) => string,
): string {
  const names = ((item.recipients && item.recipients.length ? item.recipients.map((p) => p.name) : [item.recipient_name]) as (string | undefined)[])
    .map((n) => (n || '').trim())
    .filter(Boolean);
  if (names.length === 0) return t('a_colleague', 'a colleague');
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

// Reusable people multi-select (chips + search) — for additional recipients and
// for tagging. Only colleagues with a login (user_id) can be added.
const PeoplePicker: React.FC<{
  label: string;
  placeholder: string;
  selected: workforce.EmployeeHit[];
  onChange: (list: workforce.EmployeeHit[]) => void;
  exclude?: string[];
}> = ({ label, placeholder, selected, onChange, exclude = [] }) => {
  const { t } = useTranslation('performance');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<workforce.EmployeeHit[]>([]);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 1) { setHits([]); return; }
    const id = setTimeout(() => workforce.searchEmployees(s).then((r) => setHits(r.slice(0, 6))).catch(() => setHits([])), 200);
    return () => clearTimeout(id);
  }, [q]);
  const add = (h: workforce.EmployeeHit) => {
    if (!h.user_id || exclude.includes(h.user_id) || selected.some((p) => p.user_id === h.user_id)) return;
    onChange([...selected, h]); setQ(''); setHits([]);
  };
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      {selected.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1 bg-muted rounded-full pl-1 pr-2 py-0.5 text-sm">
              <Avatar name={p.name} size={20} /> {p.name}
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => onChange(selected.filter((x) => x.user_id !== p.user_id))}><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-1.5 relative">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
        {hits.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
            {hits.map((h) => (
              <button key={h.id} type="button" onClick={() => add(h)} disabled={!h.user_id}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted text-sm disabled:opacity-50">
                <Avatar name={h.name} size={24} />
                <span className="font-medium">{h.name}</span>
                {h.sublabel && <span className="text-xs text-muted-foreground truncate">· {h.sublabel}</span>}
                {!h.user_id && <span className="ml-auto text-[10px] text-muted-foreground">{t('no_login', 'no login')}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export interface ComposerInitial { recipient?: workforce.EmployeeHit; category?: string; title?: string; message?: string }
export const GiveRecognitionComposer: React.FC<{
  initial?: ComposerInitial;
  onClose: () => void;
  onPosted: (created?: PostedRecognition) => void;
  /** Who may be RECOGNISED. Conversations narrows this to the people in that
   *  conversation — recognising someone who was never part of the discussion
   *  would be recognition from somewhere else wearing a conversation's clothes.
   *  Tagging stays directory-wide either way: notifying a manager who is not in
   *  the thread is the point of tagging. */
  searchRecipients?: (q: string) => Promise<workforce.EmployeeHit[]>;
  /** Recorded on the recognition so it can later say where it came from. */
  source?: RecognitionSource;
  /** Compact layout for a narrow surface (the conversation composer): the
   *  value chips become a dropdown. Twelve chips are three rows tall, which
   *  pushes the actions off a laptop screen. */
  compact?: boolean;
}> = ({ initial, onClose, onPosted, searchRecipients, source, compact }) => {
  const { t } = useTranslation('performance');
  const [cats, setCats] = useState<rec.RecognitionCategory[]>([]);
  const [category, setCategory] = useState(initial?.category ?? '');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<workforce.EmployeeHit[]>([]);
  const [recipient, setRecipient] = useState<workforce.EmployeeHit | null>(initial?.recipient ?? null);
  const [additional, setAdditional] = useState<workforce.EmployeeHit[]>([]);
  const [tags, setTags] = useState<workforce.EmployeeHit[]>([]);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [message, setMessage] = useState(initial?.message ?? '');
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState<rec.UploadedMedia | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState('');
  const [gifs, setGifs] = useState<rec.Gif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);

  useEffect(() => {
    rec.getRecognitionCategories().then((c) => { setCats(c); if (c[0]) setCategory((prev) => prev || c[0].key); }).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    if (!gifOpen) return;
    setGifLoading(true);
    const id = setTimeout(() => {
      rec.searchGiphy(gifQuery.trim()).then(setGifs).catch(() => setGifs([])).finally(() => setGifLoading(false));
    }, 300);
    return () => clearTimeout(id);
  }, [gifOpen, gifQuery]);

  const onPickMedia = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error(t('img_too_large', 'Image must be 8 MB or smaller.')); return; }
    setUploadingMedia(true);
    try {
      setMedia(await rec.uploadRecognitionMedia(file));
    } catch (err: any) {
      toast.error(err?.response?.status === 501 ? t('media_off', 'Image uploads aren’t enabled yet.') : getApiErrorMessage(err, t('err', 'Something went wrong')));
    } finally {
      setUploadingMedia(false);
    }
  };

  useEffect(() => {
    if (recipient) return;
    const q = query.trim();
    if (q.length < 1) { setHits([]); return; }
    const search = searchRecipients ?? workforce.searchEmployees;
    const id = setTimeout(() => search(q).then((r) => setHits(r.slice(0, 6))).catch(() => setHits([])), 200);
    return () => clearTimeout(id);
  }, [query, recipient]);

  const post = async () => {
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!recipient?.user_id || !uuidRe.test(recipient.user_id)) {
      toast.error(t('pick_recipient', 'Pick a colleague with a login to recognize.'));
      return;
    }
    if (!title.trim()) { toast.error(t('need_title', 'Add a short title.')); return; }
    setBusy(true);
    try {
      // recipient_employee_id is intentionally omitted — the server resolves
      // everything from recipient_user_id, and sending a non-uuid employee id
      // would fail strict body decoding.
      const extra = additional.map((p) => p.user_id).filter((id): id is string => !!id && id !== recipient.user_id);
      const tagged = tags.map((p) => p.user_id).filter((id): id is string => !!id && id !== recipient.user_id && !extra.includes(id));
      const saved = await rec.createRecognition({
        recipient_user_id: recipient.user_id,
        additional_recipient_user_ids: extra.length ? extra : undefined,
        tagged_user_ids: tagged.length ? tagged : undefined,
        category_key: category,
        title: title.trim(),
        message: message.trim(),
        relationship_type: 'peer',
        source_domain: source?.domain ?? 'manual',
        source_entity_type: source?.entity_type,
        source_entity_id: source?.entity_id,
        source_comment_id: source?.comment_id,
        ai_suggested: source?.ai_suggested,
        media_key: media?.media_key,
        media_type: media?.media_type,
      });
      toast.success(t('recognition_posted', 'Recognition posted 🎉'));
      // Hand the caller what was actually created, so a surface like
      // Conversations can announce it with the real names and value rather
      // than re-deriving them from its own form state.
      onPosted({
        id: (saved as { id?: string })?.id,
        recipientName: recipient.name,
        alsoNames: additional.map((p) => p.name),
        categoryLabel: cats.find((c) => c.key === category)?.label ?? '',
        title: title.trim(),
        message: message.trim(),
      });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')));
    } finally {
      setBusy(false);
    }
  };

  const [drafting, setDrafting] = useState(false);
  const localTemplate = () => {
    const who = recipient?.name || t('your_colleague', 'your colleague');
    return t('dana_draft', 'Thank you, {{who}} — your work made a real difference this week. You showed up for the team, kept things moving, and set the standard. Grateful to work with you.', { who });
  };
  const danaDraft = async () => {
    setDrafting(true);
    try {
      const catLabel = cats.find((c) => c.key === category)?.label || category;
      const msg = await rec.draftRecognition(recipient?.name || '', catLabel, message.trim());
      setMessage(msg || localTemplate()); // fall back to a local template if AI is unavailable
    } finally {
      setDrafting(false);
    }
  };

  return (
    // Sizing is COMPACT-ONLY. The Recognition page's dialog is untouched:
    // same width, same max height, same chips — only the conversation surface
    // gets the tighter frame, because only it has a thread behind it.
    <div className={`fixed inset-0 z-40 grid place-items-center bg-black/50 ${compact ? 'p-3 sm:p-4' : 'p-4'}`} onClick={onClose}>
      <div
        className={`w-full overflow-auto bg-card border border-border rounded-2xl shadow-xl ${
          compact ? 'max-w-md max-h-[85vh]' : 'max-w-lg max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold flex items-center gap-2"><Award className="w-4 h-4 text-orange-500" /> {t('give_recognition', 'Give recognition')}</h2>
          <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* recipient */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('who', 'Who are you recognizing?')}</label>
            {recipient ? (
              <div className="mt-1.5 flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                <Avatar name={recipient.name} size={28} />
                <span className="text-sm font-medium">{recipient.name}</span>
                <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => { setRecipient(null); setQuery(''); }}><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="mt-1.5 relative">
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('search_colleague', 'Search a colleague…')} />
                {hits.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
                    {hits.map((h) => (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => { setRecipient(h); setHits([]); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted text-sm"
                      >
                        <Avatar name={h.name} size={24} />
                        <span className="font-medium">{h.name}</span>
                        {h.sublabel && <span className="text-xs text-muted-foreground truncate">· {h.sublabel}</span>}
                        {!h.user_id && <span className="ml-auto text-[10px] text-muted-foreground">{t('no_login', 'no login')}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* also recognizing (multi-recipient) */}
          {recipient && (
            <PeoplePicker
              label={t('also_recognizing', 'Also recognizing (optional)')}
              placeholder={t('add_more_colleagues', 'Add more colleagues…')}
              selected={additional}
              onChange={setAdditional}
              exclude={[recipient.user_id || '', ...tags.map((p) => p.user_id || '')]}
            />
          )}
          {/* tag people (notify + visible) */}
          <PeoplePicker
            label={t('tag_people', 'Tag people to notify (e.g. their manager)')}
            placeholder={t('search_colleague', 'Search a colleague…')}
            selected={tags}
            onChange={setTags}
            exclude={[recipient?.user_id || '', ...additional.map((p) => p.user_id || '')]}
          />
          {/* recognition value */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('recognize_them_for', 'Recognize them for')}</label>
            {cats.length === 0 ? (
              <p className="mt-1.5 text-xs text-muted-foreground">{t('no_recognition_values', 'No recognition values are set up yet. An admin can add them under Performance → Admin → Recognition values.')}</p>
            ) : (
              compact ? (
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  aria-label={t('recognize_them_for', 'Recognize them for')}
                  className="mt-1.5 w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm"
                  data-testid="recognition-category"
                >
                  <option value="">{t('pick_value', 'Choose a value…')}</option>
                  {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              ) : (
              <div className="mt-1.5 flex flex-wrap gap-2" role="radiogroup" aria-label={t('recognize_them_for', 'Recognize them for')}>
                {cats.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="radio"
                    aria-checked={category === c.key}
                    onClick={() => setCategory(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      category === c.key ? 'bg-accent text-accent-foreground border-orange-300' : 'bg-muted text-muted-foreground border-transparent hover:text-foreground'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              )
            )}
          </div>
          {/* title + message */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('rec_title_field', 'Title')}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('rec_title_ph', 'e.g. Lead as one team')} className="mt-1.5" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">{t('message', 'Message')}</label>
              {/* Dictate → editable text; appends to the draft, never auto-posts. */}
              <VoiceInputButton
                onTranscript={(txt) => setMessage((m) => (m ? `${m.replace(/\s+$/, '')} ${txt}` : txt))}
                size={16}
              />
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={t('rec_msg_ph', 'Be specific — what did they do and why did it matter?')}
              className="mt-1.5 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
            <button type="button" onClick={danaDraft} disabled={drafting} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-orange-600 disabled:opacity-60">
              {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {drafting ? t('dana_drafting', 'Dana is drafting…') : t('ask_dana_draft', 'Ask Dana to draft this')}
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('add_photo', 'Add a photo or meme (optional)')}</label>
            {media ? (
              <div className="mt-1.5 relative w-fit">
                <img src={media.media_url} alt="" className="max-h-40 rounded-lg border border-border" />
                <button type="button" onClick={() => setMedia(null)} className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center rounded-full bg-card border border-border shadow" aria-label={t('remove', 'Remove')}><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="mt-1.5 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground cursor-pointer hover:text-foreground hover:border-orange-300">
                    {uploadingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                    {uploadingMedia ? t('uploading', 'Uploading…') : t('choose_image', 'Photo · PNG/JPG/GIF ≤8MB')}
                    <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onPickMedia} disabled={uploadingMedia} />
                  </label>
                  <button type="button" onClick={() => setGifOpen((v) => !v)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium ${gifOpen ? 'border-orange-300 text-orange-600 bg-accent' : 'border-dashed border-border text-muted-foreground hover:text-foreground hover:border-orange-300'}`}>🎬 {t('gif', 'GIF')}</button>
                </div>
                {gifOpen && (
                  <div className="border border-border rounded-lg p-2">
                    <Input value={gifQuery} onChange={(e) => setGifQuery(e.target.value)} placeholder={t('search_gifs', 'Search GIFs…')} className="h-8 text-sm mb-2" />
                    {gifLoading ? (
                      <div className="py-6 grid place-items-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
                    ) : gifs.length === 0 ? (
                      <div className="py-4 text-center text-xs text-muted-foreground">{t('no_gifs', 'No GIFs — try another search.')}</div>
                    ) : (
                      <div className="grid grid-cols-3 gap-1.5 max-h-52 overflow-y-auto">
                        {gifs.map((g) => (
                          <button key={g.id} type="button" onClick={() => { setMedia({ media_key: g.url, media_type: 'image/gif', media_url: g.url }); setGifOpen(false); }} className="rounded-md overflow-hidden border border-border hover:border-orange-400 transition-colors">
                            <img src={g.preview} alt={g.title || 'GIF'} loading="lazy" className="w-full h-16 object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="mt-1.5 text-[10px] text-muted-foreground text-right">{t('powered_giphy', 'Powered by GIPHY')}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 px-5 py-4 border-t border-border">
          <span className="text-[11px] text-muted-foreground">{t('flows_to_perf', 'Posts to the feed · flows into Performance')}</span>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={post} disabled={busy || !category}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('post', 'Post')}</Button>
        </div>
      </div>
    </div>
  );
};
